import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@apollo/client";
import type { ColumnDef } from "@tanstack/react-table";
import { useEffect, useState } from "react";
import SectionHeading from "@/components/SectionHeading";
import MetricGrid from "@/components/MetricGrid";
import { MetricListItem, MetricListItemGrid } from "@/components/ListItem";
import Banner from "@/components/Banner";
import ErrorBoundary from "@/components/ErrorBoundary";
import EmptyState from "@/components/EmptyState";
import Table, { TableHeading } from "@/components/Table";
import { buildTagColumn } from "@/components/Table/buildTagColumn";
import { MetricGridSkeleton, SectionHeadingSkeleton } from "@/components/Skeleton";
import { Path } from "@/router";
import { MetricCardType } from "@/components/MetricCard/MetricCard.types";
import {
  GET_MEMBERSHIP_DETAILS_QUERY,
  GET_PROSPECT_PROGRAM_QUERY,
  GET_QUALIFYING_MEMBERSHIP_APPLICATIONS_QUERY
} from "../../person/queries";
import { ProgramStatus, ProgramTypeName } from "@/types";
import type { ExperienceCreditSource, ExperienceCreditSuspensionPeriod, ExperienceCredits, Person } from "@/types";
import { toUTCFormat } from "@/lib/dateHelpers";
import { scrollToElement } from "@/lib/scrollHelpers";
import {
  buildLevelMetrics,
  buildLevelDateMetrics,
  buildProgramDetailsSectionData,
  buildProgramSummarySectionData,
  getLastActivePgmLevelNumeral,
  hasStartedProspectProgram,
  shouldShowAcceptableProgressTerminationBanner,
  LEVEL_NUMBERS
} from "@/features/person/utils/pgm-levels";
import { buildPATMetric, isPatPassed } from "@/features/person/utils/pat-metric";
import {
  PAT_ACTIVITY_SCROLL_ID,
  QUALIFYING_LEVEL_REQUIREMENTS_SCROLL_ID,
  isMembershipProgressScrollTarget
} from "@/features/person/utils/membershipProgressLinks";
import { isPatCredentialStillMetForCurrentPath } from "@/features/person/utils/pathAwareExpiration";
import { buildExperienceCreditsMetric } from "@/features/person/utils/experience-credits-metric";
import {
  buildLevelRequirements,
  omitSyntheticMemberEnrollmentOverlay,
  RequirementRow
} from "@/features/person/utils/pgm-requirements";
import {
  membershipApplicationRequirementHref,
  withMembershipApplicationLinks
} from "@/features/person/utils/membershipApplicationLinks";
import {
  buildQualifyingLevelRequirements,
  withAffiliatePathAssociateApplication
} from "@/features/person/utils/qualifyingLevelRequirements";
import {
  getActiveSuspensionFromProgress,
  getSuspensionBannerConfig,
  formatTerminationDeadline,
  getGptpProgressGuidance,
  getLevelNumeralAtSuspensionStart,
  ACCEPTABLE_PROGRESS_TERMINATION_BANNER_DESCRIPTION,
  getAcceptableProgressTerminationGuidance
} from "@/features/person/utils/pgm-suspensions";
import { buildPgmUniversityMetric } from "@/features/person/utils/pgm-university-metric";
import { buildQualifyingLevelMetric } from "@/features/person/utils/qualifyingLevelMetric";
import PATActivityTable from "@/features/person/components/PATActivityTable";
import { withBackgroundCheckRequirementLinks } from "@/features/person/components/BackgroundCheckOrders/backgroundCheckRequirementLinks";
import { canViewBackgroundCheckStaffSection } from "@/features/person/components/BackgroundCheckOrders/backgroundCheckEligibility";
import { useFlags } from "@/hooks/useFeatureFlags";
import { DOMAIN, PERMISSION, useCanViewPartialBackgroundCheck, useHasCapabilityBool } from "@/hooks/useCapabilities";

const PATH_TO_MEMBERSHIP_QUALIFYING_LEVEL_FLAG = "backoffice-ptm-qualifying-level-section" as const;
const EXPERIENCE_CREDITS_SCROLL_ID = "experience-credits-section";

const QUALIFYING_LEVEL_REQUIREMENTS_INTRO = "View all requirements and completion status for the Qualifying Level.";
const QUALIFYING_LEVEL_PROSPECT_REQUIREMENTS_BODY =
  "View all requirements and completion status for the Qualifying Level below. Prospects applying to the Associate Program for Level 1 must have eligible employment, which is tracked as part of their application. Those applying to the Affiliate program can postpone eligible employment until Level 2.";
const LEVEL_1_AFFILIATE_EMPLOYMENT_COPY =
  "Affiliates must have eligible employment to apply for the Associate program/Level 2.";

function appendHeadingBody(intro: string, extra?: string): string {
  return extra ? `${intro} ${extra}` : intro;
}

function isAffiliate(person: Person): boolean {
  return person.latestProgram?.programType?.name === ProgramTypeName.Affiliate;
}

function isProspectOrNoProgram(person: Person): boolean {
  const name = person.latestProgram?.programType?.name;
  return name == null || name === ProgramTypeName.Prospect;
}

function qualifyingLevelRequirementsBody(person: Person): string {
  return isProspectOrNoProgram(person)
    ? QUALIFYING_LEVEL_PROSPECT_REQUIREMENTS_BODY
    : QUALIFYING_LEVEL_REQUIREMENTS_INTRO;
}

function levelRequirementsBody(person: Person, n: number): string {
  const extra = n === 1 && isAffiliate(person) ? LEVEL_1_AFFILIATE_EMPLOYMENT_COPY : undefined;
  return appendHeadingBody(`View all requirements and completion status for Level ${n} below.`, extra);
}

const PATH_TO_MEMBERSHIP_METRIC_MIN_WIDTH_PX = 180;

type PersonData = { person: Person };
type SectionExpandRequest = { sectionId: string; token: number };

interface ExperienceCreditRow extends ExperienceCreditSource {
  id: string;
  overlappingSuspensions: ExperienceCreditSuspension[];
}

interface ExperienceCreditSuspension {
  id: string;
  text: string;
}

function formatExperienceCreditType(source: ExperienceCreditSource): string {
  switch (source.creditType?.toUpperCase()) {
    case "CO2":
      return "Associate's Degree";
    case "CO4":
      return "Bachelor's Degree";
    case "PGM":
      return "Student Internship";
    case "EMPLOYMENT":
      return "Employment";
    case "PRE_ASSOCIATE_EMPLOYMENT":
      return "Pre-Associate Employment";
    default:
      return source.origin === "education" ? "Academic Degree" : "--";
  }
}

function formatCreditDate(date: string | null): string {
  return date ? (toUTCFormat(date) ?? "--") : "--";
}

function getExperienceCreditSortDate(source: ExperienceCreditSource): number {
  const date = source.endedOn ?? source.startedOn;
  return date ? Date.parse(date) : 0;
}

function parseCreditDate(date: string | null): number | null {
  if (!date) return null;

  const timestamp = Date.parse(date);
  return Number.isNaN(timestamp) ? null : timestamp;
}

function hasDateRangeOverlap(
  source: ExperienceCreditSource,
  suspensionPeriod: ExperienceCreditSuspensionPeriod
): boolean {
  const sourceStart = parseCreditDate(source.startedOn ?? source.endedOn);
  const sourceEnd = parseCreditDate(source.endedOn ?? source.startedOn);
  const suspensionStart = parseCreditDate(suspensionPeriod.startedOn ?? suspensionPeriod.endedOn);
  const suspensionEnd = parseCreditDate(suspensionPeriod.endedOn ?? suspensionPeriod.startedOn);

  if (sourceStart === null || sourceEnd === null || suspensionStart === null || suspensionEnd === null) {
    return false;
  }

  return sourceStart <= suspensionEnd && suspensionStart <= sourceEnd;
}

function formatSuspensionWindow(suspensionPeriod: ExperienceCreditSuspensionPeriod): string {
  const label = suspensionPeriod.code || "Suspension";

  return `${label}: ${formatCreditDate(suspensionPeriod.startedOn)} - ${formatCreditDate(suspensionPeriod.endedOn)}`;
}

function buildOverlappingSuspensions(
  source: ExperienceCreditSource,
  suspensionPeriods: ExperienceCreditSuspensionPeriod[]
): ExperienceCreditSuspension[] {
  const overlappingSuspensions = suspensionPeriods.filter((period) => hasDateRangeOverlap(source, period));

  return overlappingSuspensions.map((suspensionPeriod, index) => ({
    id: `${suspensionPeriod.code}-${suspensionPeriod.startedOn}-${suspensionPeriod.endedOn}-${index}`,
    text: formatSuspensionWindow(suspensionPeriod)
  }));
}

function buildExperienceCreditRows(experienceCredits?: ExperienceCredits | null): ExperienceCreditRow[] {
  const suspensionPeriods = experienceCredits?.excludedSuspensionPeriods ?? [];

  return (
    experienceCredits?.sources
      .map((source, index) => ({
        ...source,
        id: `${source.origin}-${source.sourceId ?? source.creditType ?? index}`,
        overlappingSuspensions: buildOverlappingSuspensions(source, suspensionPeriods)
      }))
      .sort((first, second) => getExperienceCreditSortDate(second) - getExperienceCreditSortDate(first)) ?? []
  );
}

export default function PathToMembership() {
  const { pgaId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const flags = useFlags();
  const qualifyingLevelSectionEnabled = !!flags[PATH_TO_MEMBERSHIP_QUALIFYING_LEVEL_FLAG];
  const canViewBackgroundCheck = useCanViewPartialBackgroundCheck();
  const staffSectionVisible = canViewBackgroundCheckStaffSection({
    flags,
    canViewPartialBackgroundCheck: canViewBackgroundCheck
  });
  const canManageApplications = useHasCapabilityBool({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.manageApplications
  });
  const loadMembershipApplications = canManageApplications;
  const [sectionExpandRequest, setSectionExpandRequest] = useState<SectionExpandRequest | null>(null);

  const requestSectionExpand = (sectionId: string) => {
    setSectionExpandRequest({ sectionId, token: Date.now() });
  };

  useEffect(() => {
    if (!sectionExpandRequest) return;
    const frame = requestAnimationFrame(() => scrollToElement(sectionExpandRequest.sectionId));
    return () => cancelAnimationFrame(frame);
  }, [sectionExpandRequest]);

  const { data, error, loading } = useQuery<PersonData>(GET_MEMBERSHIP_DETAILS_QUERY, {
    variables: { pgaId },
    fetchPolicy: "cache-first"
  });

  const {
    data: prospectData,
    error: prospectError,
    loading: prospectLoading
  } = useQuery<PersonData>(GET_PROSPECT_PROGRAM_QUERY, {
    variables: { pgaId },
    fetchPolicy: "cache-first"
  });

  const {
    data: applicationsData,
    error: applicationsError,
    loading: applicationsLoading
  } = useQuery<PersonData>(GET_QUALIFYING_MEMBERSHIP_APPLICATIONS_QUERY, {
    variables: { pgaId },
    fetchPolicy: "cache-first",
    skip: !loadMembershipApplications
  });

  useEffect(() => {
    if (!qualifyingLevelSectionEnabled) return;
    if (loading || prospectLoading) return;

    const scrollTo = searchParams.get("scrollTo");
    if (!scrollTo || !isMembershipProgressScrollTarget(scrollTo)) return;

    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.delete("scrollTo");
    window.history.replaceState({}, "", currentUrl.toString());

    setSectionExpandRequest({ sectionId: scrollTo, token: Date.now() });
  }, [qualifyingLevelSectionEnabled, loading, prospectLoading, searchParams]);

  if (loading || prospectLoading) {
    return (
      <>
        <SectionHeadingSkeleton />
        <MetricGridSkeleton cards={5} minCardWidth={PATH_TO_MEMBERSHIP_METRIC_MIN_WIDTH_PX} hasHeading />
      </>
    );
  }

  const combinedError = error || prospectError;
  if (combinedError) {
    const errorMessage = combinedError.graphQLErrors?.map((e) => e.message).join(", ") || combinedError.message;
    return (
      <ErrorBoundary
        className="mt-12"
        variant="inline"
        title="Error loading path to membership data"
        message={errorMessage}
      />
    );
  }

  const membershipPerson = data?.person || ({} as Person);
  const membershipApplications = applicationsData?.person?.membershipApplications;
  const person: Person = qualifyingLevelSectionEnabled
    ? {
        ...membershipPerson,
        prospectProgram: prospectData?.person?.prospectProgram ?? membershipPerson.prospectProgram,
        accountCreatedAt: prospectData?.person?.accountCreatedAt ?? membershipPerson.accountCreatedAt,
        ...(membershipApplications ? { membershipApplications } : {})
      }
    : membershipPerson;
  const canLinkApplications = canManageApplications && !applicationsLoading && !applicationsError;
  const withApplicationLinks = (rows: RequirementRow[]) =>
    canLinkApplications ? withMembershipApplicationLinks(rows, { ...person, membershipApplications }) : rows;
  const withLevelRequirementRows = (rows: RequirementRow[], levelNumber: number) =>
    omitSyntheticMemberEnrollmentOverlay(withApplicationLinks(rows), person, levelNumber, {
      applicationsResolved: !canManageApplications || canLinkApplications
    });
  const activeSuspension = person.pgmProgressStatus ? getActiveSuspensionFromProgress(person.pgmProgressStatus) : null;
  const suspensionBannerConfig = activeSuspension ? getSuspensionBannerConfig(activeSuspension.code) : null;
  const terminationDeadline = activeSuspension ? formatTerminationDeadline(activeSuspension) : null;
  const suspensionLevelNumeral = activeSuspension
    ? getLevelNumeralAtSuspensionStart(person.pgmProgressStatus, activeSuspension)
    : null;
  const progressGuidance = activeSuspension
    ? getGptpProgressGuidance(activeSuspension.code, suspensionLevelNumeral)
    : null;
  const suspensionSubText = [progressGuidance, terminationDeadline].filter((line): line is string => Boolean(line));
  const isSuspended = !!activeSuspension && person.latestProgram?.status !== ProgramStatus.TERMINATED;
  const patData = prospectData?.person?.prospectProgram?.playingAbilityTestStatus;
  const levelMetrics = buildLevelMetrics(person, isSuspended).map((metric, index) => {
    const sectionId = `level-${LEVEL_NUMBERS[index]}-requirements-table`;
    return {
      ...metric,
      scrollToId: sectionId,
      ...(qualifyingLevelSectionEnabled && { onBeforeScroll: () => requestSectionExpand(sectionId) })
    };
  });
  const programDetails = buildProgramDetailsSectionData(person, {
    useQualifyingLevelPurchaseStart: qualifyingLevelSectionEnabled
  });
  const programSummary = buildProgramSummarySectionData(person, {
    useQualifyingLevelPurchaseStart: qualifyingLevelSectionEnabled
  });
  const hasProgramSummaryInfo = programSummary.metrics.some((metric) => Boolean(metric.value));
  const lastActiveLevelNumeral = getLastActivePgmLevelNumeral(person);
  const showAcceptableProgressTerminationBanner = shouldShowAcceptableProgressTerminationBanner(person, {
    useQualifyingLevelPurchaseStart: qualifyingLevelSectionEnabled
  });
  const patMustBeRetaken =
    !!patData?.expired && (!qualifyingLevelSectionEnabled || !isPatCredentialStillMetForCurrentPath(person, patData));
  const navigateToPatActivity = () => {
    navigate(`/people/${pgaId}/membership/prospect-program?scrollTo=${PAT_ACTIVITY_SCROLL_ID}`);
  };
  const experienceCreditsMetric = {
    ...buildExperienceCreditsMetric(person.experienceCredits, {
      scrollToId: EXPERIENCE_CREDITS_SCROLL_ID,
      ...(qualifyingLevelSectionEnabled && {
        programStarted: hasStartedProspectProgram(person)
      })
    }),
    ...(qualifyingLevelSectionEnabled && {
      onBeforeScroll: () => requestSectionExpand(EXPERIENCE_CREDITS_SCROLL_ID)
    })
  };
  const patMetric = {
    ...buildPATMetric(
      patData,
      qualifyingLevelSectionEnabled
        ? {
            scrollToId: PAT_ACTIVITY_SCROLL_ID,
            pathAwareExpiration: true,
            person
          }
        : { onClick: navigateToPatActivity }
    ),
    ...(qualifyingLevelSectionEnabled && {
      onBeforeScroll: () => requestSectionExpand(PAT_ACTIVITY_SCROLL_ID)
    })
  };
  const pgmUniversityMetric = qualifyingLevelSectionEnabled ? null : buildPgmUniversityMetric(person);
  const qualifyingLevelRequirements = qualifyingLevelSectionEnabled
    ? withBackgroundCheckRequirementLinks(buildQualifyingLevelRequirements(person), {
        staffSectionVisible,
        pgaId: pgaId ?? ""
      })
    : [];
  const metrics = qualifyingLevelSectionEnabled
    ? [
        patMetric,
        {
          ...buildQualifyingLevelMetric(person, isSuspended, qualifyingLevelRequirements, {
            useQualifyingLevelPurchaseStart: true
          }),
          scrollToId: QUALIFYING_LEVEL_REQUIREMENTS_SCROLL_ID,
          onBeforeScroll: () => requestSectionExpand(QUALIFYING_LEVEL_REQUIREMENTS_SCROLL_ID)
        },
        ...levelMetrics,
        experienceCreditsMetric
      ]
    : [...(pgmUniversityMetric ? [pgmUniversityMetric] : []), ...levelMetrics, experienceCreditsMetric, patMetric];

  const programDetailsSection: RequirementsSectionProps = {
    sectionId: "program-details-section",
    heading: programDetails.heading,
    body: programDetails.body ?? undefined,
    dateMetrics: programDetails.dateMetrics,
    dateMetricsTestId: "program-details-metrics",
    requirements: withApplicationLinks(buildLevelRequirements(person, 0, { requirementKeys: ["application"] })),
    tableTestId: "qualifying-requirements-table",
    emptyTitle: "No Qualifying Requirements",
    emptyCaption: "Qualifying requirements are not available for this person",
    hideTableWhenEmpty: true
  };

  const qualifyingLevelSection: RequirementsSectionProps = {
    sectionId: "qualifying-requirements-table",
    heading: "Qualifying Level Requirements",
    body: qualifyingLevelRequirementsBody(person),
    dateMetrics: buildLevelDateMetrics(person, 0, {
      isSuspended,
      suspensionReason: suspensionBannerConfig?.description,
      terminationTimeRemaining: activeSuspension?.timeRemaining,
      useQualifyingLevelPurchaseStart: qualifyingLevelSectionEnabled
    }),
    dateMetricsTestId: "qualifying-date-metrics",
    requirements: withApplicationLinks(qualifyingLevelRequirements),
    tableTestId: "qualifying-requirements-table",
    emptyTitle: "No Qualifying Level Requirements",
    emptyCaption: "Qualifying level requirements are not available for this person",
    collapsible: qualifyingLevelSectionEnabled,
    defaultExpanded: lastActiveLevelNumeral === 0
  };

  const levelRequirementSections: RequirementsSectionProps[] = LEVEL_NUMBERS.map((n) => ({
    sectionId: `level-${n}-requirements-table`,
    heading: `Level ${n} Requirements`,
    body: levelRequirementsBody(person, n),
    dateMetrics: buildLevelDateMetrics(person, n, {
      isSuspended,
      suspensionReason: suspensionBannerConfig?.description,
      terminationTimeRemaining: activeSuspension?.timeRemaining
    }),
    dateMetricsTestId: `level-${n}-date-metrics`,
    requirements: (() => {
      const rows = buildLevelRequirements(person, n);
      const withAssociateApplication =
        qualifyingLevelSectionEnabled && n === 1 ? withAffiliatePathAssociateApplication(rows, person) : rows;
      return withLevelRequirementRows(withAssociateApplication, n);
    })(),
    tableTestId: `level-${n}-requirements-table`,
    emptyTitle: `No Level ${n} Requirements`,
    emptyCaption: `Level ${n} requirements are not available for this person`,
    collapsible: qualifyingLevelSectionEnabled,
    defaultExpanded: lastActiveLevelNumeral === n
  }));

  const requirementsSections: RequirementsSectionProps[] = qualifyingLevelSectionEnabled
    ? [qualifyingLevelSection, ...levelRequirementSections]
    : [programDetailsSection, ...levelRequirementSections];

  return (
    <>
      {isSuspended && suspensionBannerConfig && (
        <Banner
          variant="warn"
          description={suspensionBannerConfig.description}
          subText={suspensionSubText.length > 0 ? suspensionSubText : undefined}
          testId="suspension-banner"
          primaryAction={{
            label: "View Suspension History",
            link: `/people/${pgaId}/membership/overview?scrollTo=suspension-history-table`,
            isInternal: true
          }}
          hideSecondaryDismiss
          className="mt-8"
          inline
        />
      )}
      {showAcceptableProgressTerminationBanner && (
        <Banner
          variant="error"
          description={ACCEPTABLE_PROGRESS_TERMINATION_BANNER_DESCRIPTION}
          subText={getAcceptableProgressTerminationGuidance(lastActiveLevelNumeral, patMustBeRetaken)}
          testId="acceptable-progress-termination-banner"
          hideSecondaryDismiss
          className="mt-8"
          inline
        />
      )}
      {applicationsError && (
        <Banner
          variant="warn"
          description="Membership application links could not be loaded."
          subText="You can still review Path to Membership."
          testId="membership-applications-link-banner"
          hideSecondaryDismiss
          className="mt-8"
          inline
        />
      )}
      {qualifyingLevelSectionEnabled ? (
        <>
          <SectionHeading
            heading={programSummary.heading}
            body={
              <>
                View a snapshot of this person's progress towards election to PGA of America Membership. To maintain
                privacy, <strong>please limit the information you share with employers</strong> to high-level updates
                only. If an employer requests more specific information, please direct them to speak with the Associate.
              </>
            }
          />
          {hasProgramSummaryInfo && <ProgramSummaryMetrics metrics={programSummary.metrics} />}
        </>
      ) : (
        <SectionHeading
          heading="Path to Membership"
          body={
            <>
              View a snapshot of this person's progress towards election to PGA of America Membership. To maintain
              privacy, <strong>please limit the information you share with employers</strong> to high-level updates
              only. If an employer requests more specific information, please direct them to speak with the Associate.
            </>
          }
        />
      )}
      {qualifyingLevelSectionEnabled && hasProgramSummaryInfo && (
        <SectionHeading heading="At a Glance" body="Press a card to quickly access its corresponding details." />
      )}
      <MetricGrid metrics={metrics} minCardWidth={PATH_TO_MEMBERSHIP_METRIC_MIN_WIDTH_PX} hasHeading />
      {qualifyingLevelSectionEnabled && (
        <div id={PAT_ACTIVITY_SCROLL_ID}>
          <PATActivityTable
            patStatus={patData}
            loading={false}
            enableNavigation={true}
            pgaId={pgaId}
            personId={person.id}
            person={person}
            pathAwareExpiration
            collapsible
            defaultExpanded={!isPatPassed(patData, { pathAwareExpiration: true, person })}
            expandRequestToken={
              sectionExpandRequest?.sectionId === PAT_ACTIVITY_SCROLL_ID ? sectionExpandRequest.token : undefined
            }
          />
        </div>
      )}
      {requirementsSections.map((section) => (
        <RequirementsSection
          key={section.sectionId}
          {...section}
          expandRequestToken={
            sectionExpandRequest?.sectionId === section.sectionId ? sectionExpandRequest.token : undefined
          }
        />
      ))}
      <ExperienceCreditsSection
        experienceCredits={person.experienceCredits}
        collapsible={qualifyingLevelSectionEnabled}
        expandRequestToken={
          sectionExpandRequest?.sectionId === EXPERIENCE_CREDITS_SCROLL_ID ? sectionExpandRequest.token : undefined
        }
      />
    </>
  );
}

function toMetricListItemProps(metric: MetricCardType<Path>) {
  return {
    iconName: metric.iconName ?? "info",
    overline: metric.label,
    headline: metric.value ?? "--",
    tooltipTitle: typeof metric.tooltip?.title === "string" ? metric.tooltip.title : undefined,
    tooltipSupportingText:
      typeof metric.tooltip?.supportingText === "string" ? metric.tooltip.supportingText : undefined
  };
}

function ProgramSummaryMetrics({ metrics }: { metrics: MetricCardType<Path>[] }) {
  if (metrics.length === 0) return null;

  return (
    <MetricListItemGrid className="-mt-2" testId="program-summary-metrics">
      {metrics.map((metric) => (
        <MetricListItem key={metric.label} {...toMetricListItemProps(metric)} />
      ))}
    </MetricListItemGrid>
  );
}

const experienceCreditColumns: ColumnDef<ExperienceCreditRow, unknown>[] = [
  {
    accessorKey: "description",
    header: "Description",
    cell: ({ row }) => <span>{row.original.description || "--"}</span>,
    enableSorting: false,
    size: 220
  },
  {
    accessorKey: "creditType",
    header: "Type",
    cell: ({ row }) => <span>{formatExperienceCreditType(row.original)}</span>,
    enableSorting: false,
    size: 180
  },
  {
    accessorKey: "credits",
    header: "Credits",
    enableSorting: false,
    size: 100
  },
  {
    accessorKey: "startedOn",
    header: "Credit Start Date",
    cell: ({ row }) => <span>{formatCreditDate(row.original.startedOn)}</span>,
    enableSorting: false,
    size: 140
  },
  {
    accessorKey: "endedOn",
    header: "Credit End Date",
    cell: ({ row }) => <span>{formatCreditDate(row.original.endedOn)}</span>,
    enableSorting: false,
    size: 140
  },
  {
    accessorKey: "overlappingSuspensions",
    header: "Adjustments",
    cell: ({ row }) => {
      const suspensions = row.original.overlappingSuspensions;

      if (!suspensions.length) {
        return <span>--</span>;
      }

      return (
        <div className="flex flex-col gap-1 whitespace-normal">
          {suspensions.map((suspension) => (
            <span key={suspension.id}>{suspension.text}</span>
          ))}
        </div>
      );
    },
    enableSorting: false,
    size: 240,
    minSize: 240,
    meta: {
      className: "max-lg:flex-col max-lg:items-start max-lg:gap-1"
    }
  }
];

const requirementsColumns = [
  {
    accessorKey: "name",
    header: "Requirement",
    cell: ({ row }: { row: { original: RequirementRow } }) => <span>{row.original.name}</span>,
    enableSorting: false,
    size: 300
  },
  {
    accessorKey: "completionDate",
    header: "Completion Date",
    cell: ({ row }: { row: { original: RequirementRow } }) => <span>{row.original.completionDate || "--"}</span>,
    enableSorting: false,
    size: 150
  },
  buildTagColumn<RequirementRow>({
    accessorKey: "status",
    header: "Status",
    getTagLabel: (row) => row.status,
    getTagType: (row) => row.statusType,
    getIconName: (row) => (row.status === "Locked" ? "lock" : undefined),
    getTooltipText: (row) => (row.status === "Locked" ? "Previous Requirement(s) Must Be Met First" : undefined),
    getTooltip: (row) => row.statusTooltip,
    size: 120
  })
];

function ExperienceCreditsSection({
  experienceCredits,
  collapsible = false,
  expandRequestToken
}: {
  experienceCredits?: ExperienceCredits | null;
  collapsible?: boolean;
  expandRequestToken?: number;
}) {
  const [expanded, setExpanded] = useState(!collapsible);
  const showTable = !collapsible || expanded;
  const rows = buildExperienceCreditRows(experienceCredits);
  const totalCreditsHeadline = experienceCredits
    ? experienceCredits.threshold != null
      ? `${experienceCredits.total} of ${experienceCredits.threshold}`
      : String(experienceCredits.total)
    : "--";
  const sectionBody =
    experienceCredits?.threshold != null
      ? `View all activities for this person that qualify as experience credits toward their eligibility for membership. A minimum of ${experienceCredits.threshold} credits is required to meet the membership application requirement.`
      : "View all activities for this person that qualify as experience credits toward their eligibility for membership.";

  useEffect(() => {
    if (expandRequestToken != null) setExpanded(true);
  }, [expandRequestToken]);

  return (
    <div id={EXPERIENCE_CREDITS_SCROLL_ID}>
      <TableHeading
        heading="Experience Credits"
        body={sectionBody}
        collapsible={collapsible}
        defaultExpanded={!collapsible}
        onExpandedChange={setExpanded}
        expanded={collapsible ? expanded : undefined}
      />
      <MetricListItemGrid className="-mt-2" testId="experience-credits-metrics">
        <MetricListItem iconName="collections_bookmark" overline="Total Credits" headline={totalCreditsHeadline} />
        <MetricListItem
          iconName="school"
          overline="Education Credits"
          headline={experienceCredits ? String(experienceCredits.educationCredits) : "--"}
        />
        <MetricListItem
          iconName="business_center"
          overline="Employment Credits"
          headline={experienceCredits ? String(experienceCredits.employmentCredits) : "--"}
        />
      </MetricListItemGrid>
      {showTable && (
        <div className="mt-6">
          <Table
            data={rows}
            columns={experienceCreditColumns}
            loading={false}
            data-testid="experience-credits-table"
            renderNoDataEmptyState={() => (
              <EmptyState
                title="No Experience Credits"
                caption="No experience credits have been recorded yet"
                iconName="collections_bookmark"
              />
            )}
          />
        </div>
      )}
    </div>
  );
}

interface RequirementsSectionProps {
  sectionId: string;
  heading: string;
  body?: string;
  dateMetrics: MetricCardType<Path>[];
  dateMetricsTestId: string;
  requirements: RequirementRow[];
  tableTestId: string;
  emptyTitle: string;
  emptyCaption: string;
  hideTableWhenEmpty?: boolean;
  collapsible?: boolean;
  defaultExpanded?: boolean;
  expandRequestToken?: number;
}

function RequirementsSection({
  sectionId,
  heading,
  body,
  dateMetrics,
  dateMetricsTestId,
  requirements,
  tableTestId,
  emptyTitle,
  emptyCaption,
  hideTableWhenEmpty,
  collapsible = false,
  defaultExpanded = true,
  expandRequestToken
}: RequirementsSectionProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const showTable = !(hideTableWhenEmpty && requirements.length === 0) && (!collapsible || expanded);

  useEffect(() => {
    if (expandRequestToken != null) setExpanded(true);
  }, [expandRequestToken]);

  return (
    <>
      <div id={sectionId}>
        <TableHeading
          heading={heading}
          body={body}
          collapsible={collapsible}
          defaultExpanded={defaultExpanded}
          expanded={collapsible ? expanded : undefined}
          onExpandedChange={setExpanded}
        />
      </div>
      <MetricListItemGrid className="-mt-2" testId={dateMetricsTestId}>
        {dateMetrics.map((metric) => (
          <MetricListItem key={metric.label} {...toMetricListItemProps(metric)} />
        ))}
      </MetricListItemGrid>
      {showTable && (
        <div className="mt-6">
          <Table
            data={requirements}
            columns={requirementsColumns}
            loading={false}
            data-testid={tableTestId}
            getRowLink={(row) => {
              const href = membershipApplicationRequirementHref(row.original);
              if (href) return { to: href as Path };
              if (row.original.detailPath) {
                return { to: row.original.detailPath };
              }
              return null;
            }}
            onClickRow={(row) => {
              const sectionId = row.original.scrollToSectionId;
              if (sectionId) scrollToElement(sectionId);
            }}
            renderNoDataEmptyState={() => <EmptyState title={emptyTitle} caption={emptyCaption} iconName="school" />}
          />
        </div>
      )}
    </>
  );
}
